import { ApiBase } from "@/src/model/ApiBase";
import { CHAT_API_TYPE } from "@/src/model/apiTypes";

export type Message =
  | { message: TextMessage }
  | { tool: ToolMessage }
  | { error: Error }
  | { thought: Thought }
  | { rules: Rule[] };

export type Thread = {
  id: number;
  name: string;
};
export type RichMessage = Message & { thread: Thread };

export type BaseMessage = {
  id: number;
  created: Date;
};

export type TextMessage = BaseMessage & {
  text: string;
  role: string;
};
export type ToolMessage = BaseMessage & {
  content: string;
  role: string;
};
export type Error = BaseMessage & {
  text: string;
  raw: any;
};
export type Thought = BaseMessage & {
  text: string;
  visibility: "visible" | "hidden";
};
export type Rule = BaseMessage & {
  id: number;
  valid: boolean;
  syntax: string;
  state: "pending" | "dismissed" | "accepted";
  replace: string[];
};

export type Conversation = {
  id: number;
  created: Date;
  title: string;
};

export type AdminConversation = Conversation & {
  broken: boolean;
  feedback: Feedback;
  error: string;
  logs: string;
  title: string;
  messagesAmount: number;
  allMessagesAmount: number;
  user: {
    username: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    displayName: string;
  };
  meta: {
    graph_id: number;
  };
};

export type Feedback = {
  sentiment: "neutral" | "positive" | "negative";
  text: string;
};

export type DetailedConversation = {
  messages: Message[];
  feedback: Feedback;
};

export type HealthSummary = {
  agent: string;
  name: string;
  healthy: boolean;
  message: string;
}[];

export type GrammarExample = {
  id: number;
  task: string;
  rule: string;
  explanation: string;
  use: boolean;
  valid: "valid" | "invalid" | "pending";
  error: string;
};

export class ChatApi extends ApiBase {
  getUrl(root: any, path: string = "") {
    let url = `graphs/${root.repoStore.selectedGraphId}/llm/conversations`;
    if (path) {
      url += `/${path}`;
    }
    return url;
  }

  async [CHAT_API_TYPE.ALL_CONVERSATIONS]({
    root,
  }: {
    root: any;
  }): Promise<Conversation[]> {
    let result = await this.httpGet(this.getUrl(root));
    return result as Conversation[];
  }

  async [CHAT_API_TYPE.NEW_CONVERSATION]({
    root,
  }: {
    root: any;
  }): Promise<Conversation> {
    let result = await this.httpPut(this.getUrl(root, "new"));
    return result as Conversation;
  }

  async [CHAT_API_TYPE.GET_CONVERSATION]({
    root,
    allMessages = false,
  }: {
    root: any;
    allMessages: boolean;
  }): Promise<DetailedConversation> {
    let url = this.getUrl(root, root.chatbotStore.conversationId);
    if (allMessages) {
      url += "?allMessages=true";
    }
    let result = await this.httpGet(url);
    return result as DetailedConversation;
  }

  async [CHAT_API_TYPE.SEND_MESSAGE]({
    root,
    message,
    context,
  }: {
    root: any;
    message: string;
    context: any;
  }): Promise<Message> {
    let result = await this.httpPost(
      this.getUrl(root, root.chatbotStore.conversationId),
      {
        message: window.btoa(encodeURIComponent(message)),
        context: window.btoa(encodeURIComponent(JSON.stringify(context))),
      }
    );
    console.log("SEND_MESSAGE result", result);
    return result as Message;
  }

  async [CHAT_API_TYPE.POLLING_THOUGHTS]({
    root,
  }: {
    root: any;
  }): Promise<string[]> {
    let result = await this.httpGet(
      this.getUrl(root, `${root.chatbotStore.conversationId}/thoughts`)
    );
    return result as string[];
  }

  async [CHAT_API_TYPE.PROVIDE_FEEDBACK]({
    root,
    feedback,
  }: {
    root: any;
    feedback;
  }): Promise<void> {
    let result = await this.httpPost(
      this.getUrl(root, `${root.chatbotStore.conversationId}/feedback`),
      feedback
    );
  }

  async [CHAT_API_TYPE.GET_HEALTH_SUMMARY]({
    root,
  }: {
    root: any;
  }): Promise<HealthSummary> {
    let result = await this.httpGet(
      this.getUrl(root, `${root.chatbotStore.conversationId}/health`)
    );
    return result as HealthSummary;
  }

  async [CHAT_API_TYPE.GET_CONTEXT_CACHE]({
    root,
  }: {
    root: any;
  }): Promise<any> {
    let result = await this.httpGet(
      this.getUrl(root, `${root.chatbotStore.conversationId}/contextcache`)
    );
    return result as any;
  }

  async [CHAT_API_TYPE.DELETE_CONTEXT_CACHE]({
    root,
  }: {
    root: any;
  }): Promise<void> {
    await this.httpDelete(
      this.getUrl(root, `${root.chatbotStore.conversationId}/contextcache`)
    );
  }

  async [CHAT_API_TYPE.ADMIN_LOAD_ALL_CONVERSATIONS]({
    root,
  }: {
    root: any;
  }): Promise<AdminConversation[]> {
    let result = await this.httpGet("admin/chatbot/conversations");
    console.log("GET ADMIN CONVERSATIONS", result);
    return result as AdminConversation[];
  }

  async [CHAT_API_TYPE.IS_CHATBOT_ENABLED](): Promise<boolean> {
    let result = await this.httpGet("chatbot");
    return result.enabled as boolean;
  }

  async [CHAT_API_TYPE.ADMIN_LOAD_ALL_EXAMPLES](): Promise<GrammarExample[]> {
    let result = await this.httpGet("admin/chatbot/examples");
    return result.map(o => {
      o.valid = o.valid ? "valid" : "invalid";
      return o;
    }) as GrammarExample[];
  }

  async [CHAT_API_TYPE.ADMIN_UPDATE_EXAMPLE]({
    example,
  }: {
    example: GrammarExample;
  }): Promise<GrammarExample> {
    let result = await this.httpPost("admin/chatbot/examples", example);
    result.valid = result.valid ? "valid" : "invalid";
    return result as GrammarExample;
  }

  async [CHAT_API_TYPE.ADMIN_DELETE_EXAMPLE]({
    id,
  }: {
    id: number;
  }): Promise<void> {
    await this.httpDelete(`admin/chatbot/examples/${id}`);
  }
}
